import { Module } from "vuex";
import studentExamsService from "@/services/studentExamsService";
import { StudentExam, StudentExamsQueryParams } from "@/interfaces/StudentExam";
import { Platform } from "@/interfaces/Platform";

interface StudentExamsState {
  studentExams: StudentExam[];
  filters: object;
  breadcrumbs: any[];
  selectedPlatform: Platform | null;
  loading: boolean;
  loadingStudentExamId: number | null;
  params: StudentExamsQueryParams; // Query parameters for API
  meta: PaginationMeta; // Pagination metadata
  error: string | null; // Error handling
}

const studentExamsModule: Module<StudentExamsState, any> = {
  namespaced: true,

  // State: Add flexibility to the params
  state: (): StudentExamsState => ({
    studentExams: [],
    filters: {},
    breadcrumbs: [],
    selectedPlatform: null,
    loading: false,
    loadingStudentExamId: null,
    params: {
      name: "", // Search query
      round: undefined,
      status: undefined,
      sort: "id",
      order: "asc",
      page: 1,
    },
    meta: {
      current_page: 0,
      last_page: 0,
      per_page: 0,
      total: 0,
      first_page_number: 1,
      last_page_number: 0,
      next_page_number: null,
      prev_page_number: null,
      pages: [],
    },
    error: null,
  }),

  // Mutations
  mutations: {
    setStudentExams(state, studentExams: StudentExam[]) {
      state.studentExams = studentExams;
    },
    setLoading(state, loading: boolean) {
      state.loading = loading;
    },
    setLoadingStudentExam(state, studentExamId: number | null) {
      state.loadingStudentExamId = studentExamId;
    },
    setParams(state, params: Partial<StudentExamsState["params"]>) {
      state.params = { ...state.params, ...params };
    },
    setBreadcrumbs(state, breadcrumbs: any[]) {
      state.breadcrumbs = breadcrumbs;
    },
    setFilters(state, filters: object) {
      state.filters = filters;
    },
    setPaginationMeta(state, meta: PaginationMeta) {
      state.meta = meta;
    },
    setError(state, error: string | null) {
      state.error = error;
    },
  },

  // Actions
  actions: {
    async fetchStudentExams({ commit, state }, { url }) {
      commit("setLoading", true);
      try {
        const response = await studentExamsService.getStudentExams(
          url,
          state.params
        );
        commit("setStudentExams", response.data.data);
        commit("setFilters", response.data.filters || {});
        commit("setPaginationMeta", response.data.meta);
        commit("setBreadcrumbs", response.data.breadcrumbs || []);
      } catch (error) {
        commit("setError", error || "Failed to fetch student's exams");
      } finally {
        commit("setLoading", false);
      }
    },

    updateParams({ commit, dispatch }, { url, newParams }) {
      commit("setParams", newParams);
      dispatch("fetchStudentExams", { url }); // Pass URL when updating params
    },
  },

  // Getters: Add support for accessing new states
  getters: {
    allStudentExams: (state) => state.studentExams,
    isLoading: (state) => state.loading,
    hasError: (state) => !!state.error,
    errorMessage: (state) => state.error,
    loadingStudentExamId: (state) => state.loadingStudentExamId,
    filters: (state) => state.filters,
    params: (state) => state.params, // Expose params for debugging
  },
};

export default studentExamsModule;
